/**
 * Schema-driven readers from lossless JSON nodes to the decoded RPE types (W1-07a formats-json-rpe), shared by
 * `decodeRpe` and `decodeRpeFragment`.
 *
 * Lenient by design: every deviation from what Phira's serde structs accept is recorded as a DecodeDiagnostic and
 * decoding continues. Severities: 'phiraRejects' for fields Phira reads (its JSON parse would fail), 'phiraPanics'
 * where Phira crashes (control point without its value key), 'note' for fields Phira ignores (RPE-only data with an
 * unexpected type), for the META strings only Phira's metadata inference reads (it skips them silently, F§2.2) and
 * for problems Phira loads silently (a beat triple with c = 0).
 *
 * Diagnostics never carry English text: `detail` holds raw source lexemes and values only (`1.0`, `"Pause"`,
 * `[1, 1, 0]`), and wrong types carry structured codes (`expected`, `found`, `reason`; RpeDiagnostic) that
 * `diagnosticRef` (decode.ts) turns into translatable messages.
 *
 * Values are kept as written wherever the decoded type can hold them (a float `1.5` in an integer field stays
 * 1.5, a triple `[1, -1, 0]` stays as is) so that normalisation can repair them and report the repair. Missing
 * required fields get RPE's default; a malformed beat triple becomes [NaN, NaN, NaN] so that normalisation
 * refuses the item. Unknown keys go to `extra` in source order (duplicates: the last one wins). A judge line entry
 * that is not an object becomes an empty placeholder line, so the indices of the following lines (and the `father`
 * references to them) stay as written.
 *
 * Integer typing (i32 / u8 / u16 / u32) comes from RPE_SCHEMA (`intKindOf`). serde_json reads the integer lexeme
 * `-0` as the float -0.0, so it is a float in an integer field like `0.0` ('floatInIntField'). Numbers beyond the
 * f64 range (`1e400`; JSON.parse gives an infinity) are reported as 'wrongType' / 'numberOverflow': 'phiraRejects'
 * where serde_json reads the number (typed fields, every key of a control point, RPEVersion), 'note' where Phira
 * skips the value (unknown keys, RPE-only fields, skewControl). Data kept as written (passthrough, skewControl,
 * RPEVersion) holds the largest finite double of the same sign instead (`passthroughJson`), so every decoded value
 * can be written back.
 *
 * Text: an unpaired surrogate escape (`\uD800` without its pair) cannot be written as UTF-8 (TextEncoder makes it
 * U+FFFD), and serde_json refuses it in every string it deserialises: typed text (Name, Texture, hitsound, text
 * events, RPEVersion) and the keys of every object Phira parses ('phiraRejects'). It skips the values of unknown
 * keys and RPE-only fields without checking them ('note'). Either way the decoded string holds U+FFFD in its place
 * and the finding is reported as 'wrongType' / 'unpairedSurrogate', so normalisation reports the change and the
 * chart reads back unchanged after encoding.
 *
 * Duplicate keys: serde refuses a duplicate struct field (also `x` / `easing` next to a control point's flattened
 * map: 'wrongType' / 'duplicateKey'); duplicate unknown keys and a control point's value or extra keys keep the last
 * value, like Phira's maps. Unknown keys of an RPE time tag cannot be kept (the IR has no place for them; Phira
 * ignores time tags): 'note' / 'notKept'.
 */

import { setOwn } from '../../core/util/json';
import type { Json, Passthrough } from '../../core/util/types';
import { hasLoneSurrogate, isSerdeIntegerLexeme, nodeToJson, toWellFormedString } from '../json/lexer';
import type { JsonNode } from '../json/types';
import type {
  DecodeDiagnostic,
  DecodeSeverity,
  RpeBpmItem,
  RpeChart,
  RpeControlPoint,
  RpeEvent,
  RpeEventLayer,
  RpeExtended,
  RpeJudgeLine,
  RpeMeta,
  RpeNote,
  RpeTriple,
  UiElementName,
} from './json';
import {
  CONTROL_CURVE_KEYS,
  CONTROL_CURVES,
  EXTENDED_LIST_KEYS,
  eventValueKind,
  fieldSpec,
  isKnownKey,
  LAYER_LIST_KEYS,
  UI_ELEMENT_NAMES,
} from './schema';
import type { ControlValueKey, EventListKey, RpeFieldSpecEx, RpeObjectKind } from './schema';

type Mutable<T> = { -readonly [K in keyof T]: T[K] };
type NumNode = Extract<JsonNode, { k: 'num' }>;
type StrNode = Extract<JsonNode, { k: 'str' }>;
type ObjNode = Extract<JsonNode, { k: 'obj' }>;
type ArrNode = Extract<JsonNode, { k: 'arr' }>;

/** A beat triple that normalisation refuses (malformed in the source). */
export const INVALID_TRIPLE: RpeTriple = [Number.NaN, Number.NaN, Number.NaN];

const INT_RANGES = {
  i32: [-2147483648, 2147483647],
  u8: [0, 255],
  u16: [0, 65535],
  u32: [0, 4294967295],
} as const;
type IntKind = keyof typeof INT_RANGES;

/** What a value should have been (wrongType diagnostics; translated through `diagnosticRef`). */
export type ExpectedKind = NonNullable<DecodeDiagnostic['expected']>;

/** The JSON kind actually found (JsonNode `k`). */
export type FoundKind = NonNullable<DecodeDiagnostic['found']>;

/**
 * Why a 'wrongType' diagnostic was raised when it is not a plain type mismatch: 'outOfRange' = a whole number
 * outside its Rust integer type; 'numberOverflow' = a number beyond the f64 range (`1e400`), which serde_json
 * refuses wherever it reads the value ("number out of range") and which JSON.parse turns into an infinity;
 * 'unpairedSurrogate' = text with half of a UTF-16 surrogate pair (`\uD800`), replaced by U+FFFD; 'notKept' = a
 * member the decoded chart has no place for (unknown keys of an RPE time tag), dropped.
 */
export type DiagnosticReason = NonNullable<DecodeDiagnostic['reason']>;

/**
 * A DecodeDiagnostic with structured, language-neutral details (`detail` stays raw lexemes / values only); the
 * structured fields are part of the contract since CR-W1-07a-9 / -13 / -15.
 */
export type RpeDiagnostic = DecodeDiagnostic;

export interface DiagnosticInfo {
  readonly expected?: ExpectedKind;
  readonly found?: FoundKind;
  readonly reason?: DiagnosticReason;
}

/** Longest string value quoted in a diagnostic detail (longer ones are cut and end in "..."). */
const DETAIL_STRING_MAX = 60;

/** The raw source form of a scalar node (diagnostic detail); undefined for null, lists and objects. */
export function scalarLexeme(node: JsonNode): string | undefined {
  switch (node.k) {
    case 'num':
      return node.lex;
    case 'bool':
      return node.v ? 'true' : 'false';
    case 'str': {
      const quoted = JSON.stringify(node.v);
      return quoted.length <= DETAIL_STRING_MAX ? quoted : `${quoted.slice(0, DETAIL_STRING_MAX - 3)}...`;
    }
    case 'null':
    case 'arr':
    case 'obj':
      return undefined;
  }
}

/** A JSON path, or a function building it (paths are only needed for diagnostics: built on demand). */
export type LazyPath = string | (() => string);

export function pathOf(p: LazyPath): string {
  return typeof p === 'string' ? p : p();
}

export function childPath(parent: LazyPath, key: string | number): string {
  const base = pathOf(parent);
  if (typeof key === 'number') return `${base}[${String(key)}]`;
  return base === '' ? key : `${base}.${key}`;
}

/** Lazy child path. */
function sub(parent: LazyPath, key: string | number): () => string {
  return () => childPath(parent, key);
}

/** Collects diagnostics while reading. */
export class Reader {
  readonly diagnostics: RpeDiagnostic[] = [];

  diag(
    severity: DecodeSeverity,
    lazyPath: LazyPath,
    code: DecodeDiagnostic['code'],
    detail?: string,
    info: DiagnosticInfo = {},
  ): void {
    const d: Mutable<RpeDiagnostic> = { severity, path: pathOf(lazyPath), code };
    if (detail !== undefined) d.detail = detail;
    if (info.expected !== undefined) d.expected = info.expected;
    if (info.found !== undefined) d.found = info.found;
    if (info.reason !== undefined) d.reason = info.reason;
    this.diagnostics.push(d);
  }

  wrongType(severity: DecodeSeverity, path: LazyPath, expected: ExpectedKind, node: JsonNode): void {
    this.diag(severity, path, 'wrongType', scalarLexeme(node), { expected, found: node.k });
  }
}

/** 'note' where Phira loads the chart whatever the value is (see the module header), else 'phiraRejects'. */
function severityOf(spec: RpeFieldSpecEx | undefined): DecodeSeverity {
  return spec?.phira === 'ignores' || spec?.readsOutsideParser === true ? 'note' : 'phiraRejects';
}

/** The Rust integer type of a schema field (the readers take integer typing from RPE_SCHEMA). */
export function intKindOf(kind: RpeObjectKind, key: string): IntKind {
  const k = fieldSpec(kind, key)?.kind;
  if (k === 'i32' || k === 'u8' || k === 'u16' || k === 'u32') return k;
  throw new Error(`RPE_SCHEMA: ${kind}.${key} is not an integer field`);
}

/**
 * The finite number that stands in for a number beyond the f64 range (JSON `1e400` / `-1e400`): the largest
 * finite double of the same sign. JSON cannot hold an infinity, and the writer refuses one.
 */
export function overflowStandIn(v: number): number {
  return v < 0 ? -Number.MAX_VALUE : Number.MAX_VALUE;
}

/**
 * A text value: an unpaired surrogate is reported ('wrongType' / 'unpairedSurrogate' with `severity`) and replaced
 * by U+FFFD (module header). With `r` null nothing is reported.
 */
export function readText(r: Reader | null, node: StrNode, path: LazyPath, severity: DecodeSeverity): string {
  if (!hasLoneSurrogate(node.v)) return node.v;
  r?.diag(severity, path, 'wrongType', scalarLexeme(node), {
    reason: 'unpairedSurrogate',
    expected: 'text',
    found: 'str',
  });
  return toWellFormedString(node.v);
}

/** An object key: like `readText` (serde_json checks the keys of every object it parses into a struct or map). */
export function readKey(r: Reader | null, key: string, path: LazyPath, severity: DecodeSeverity): string {
  if (!hasLoneSurrogate(key)) return key;
  r?.diag(severity, path, 'wrongType', JSON.stringify(key), { reason: 'unpairedSurrogate', found: 'str' });
  return toWellFormedString(key);
}

/**
 * Plain JSON of a value kept as written (passthrough keys, skewControl, the RPEVersion value): like `nodeToJson`
 * (duplicate keys: the last one wins), except that a number beyond the f64 range becomes `overflowStandIn` and is
 * reported at its own path as 'wrongType' / 'numberOverflow', and an unpaired surrogate in a string or key becomes
 * U+FFFD ('wrongType' / 'unpairedSurrogate'), both with `severity` ('note' for data Phira skips, which serde_json
 * ignores without reading it). With `r` null nothing is reported (the caller already reported the whole value).
 */
export function passthroughJson(r: Reader | null, node: JsonNode, path: LazyPath, severity: DecodeSeverity): Json {
  switch (node.k) {
    case 'num':
      if (Number.isFinite(node.v)) return node.v;
      r?.diag(severity, path, 'wrongType', node.lex, { reason: 'numberOverflow', expected: 'number', found: 'num' });
      return overflowStandIn(node.v);
    case 'arr':
      return node.items.map((item, i) => passthroughJson(r, item, sub(path, i), severity));
    case 'obj': {
      const keys = node.entries.map(([key]) => readKey(r, key, sub(path, toWellFormedString(key)), severity));
      const last = new Map<string, number>();
      keys.forEach((key, i) => last.set(key, i));
      const out: Record<string, Json> = {};
      node.entries.forEach(([, value], i) => {
        const key = keys[i] ?? '';
        // Earlier duplicates are overridden (and never reported); the key keeps its first position, as JSON.parse.
        setOwn(out, key, last.get(key) === i ? passthroughJson(r, value, sub(path, key), severity) : null);
      });
      return out;
    }
    case 'str':
      return readText(r, node, path, severity);
    case 'null':
    case 'bool':
      return nodeToJson(node);
  }
}

/** Members of an object node, split into known keys (by schema) and passthrough. */
export class ObjView {
  private readonly lazyPath: LazyPath;
  private readonly kind: RpeObjectKind;
  private readonly known = new Map<string, JsonNode>();
  readonly extra: Passthrough;
  private readonly r: Reader;

  constructor(r: Reader, node: ObjNode, path: LazyPath, kind: RpeObjectKind) {
    this.r = r;
    this.lazyPath = path;
    this.kind = kind;
    let extra: Map<string, JsonNode> | null = null;
    for (const [key, value] of node.entries) {
      if (isKnownKey(kind, key)) {
        if (this.known.has(key)) {
          r.diag(severityOf(fieldSpec(kind, key)), sub(path, key), 'wrongType', undefined, { reason: 'duplicateKey' });
        }
        this.known.set(key, value);
      } else {
        // serde_json reads every key of a struct it parses, unknown ones included: an unpaired surrogate there
        // refuses the chart. Known keys are plain ASCII names.
        const k = readKey(r, key, sub(path, toWellFormedString(key)), 'phiraRejects');
        extra ??= new Map<string, JsonNode>();
        extra.delete(k);
        extra.set(k, value);
      }
    }
    // Unknown keys: Phira skips them (serde ignores unknown fields without reading their numbers): 'note'.
    this.extra =
      extra === null
        ? []
        : [...extra.entries()].map(([key, value]) => [key, passthroughJson(r, value, sub(path, key), 'note')] as const);
  }

  get path(): string {
    return pathOf(this.lazyPath);
  }

  has(key: string): boolean {
    return this.known.has(key);
  }

  node(key: string): JsonNode | undefined {
    return this.known.get(key);
  }

  spec(key: string): RpeFieldSpecEx | undefined {
    return fieldSpec(this.kind, key);
  }

  sub(key: string): () => string {
    return sub(this.lazyPath, key);
  }

  /**
   * The node of `key`, handling absence and null: undefined when absent (missing required -> diagnostic),
   * null when JSON null (a type error unless the field is nullable).
   */
  get(key: string): JsonNode | null | undefined {
    const node = this.known.get(key);
    const spec = this.spec(key);
    if (node === undefined) {
      if (spec?.required === 'phira') this.r.diag('phiraRejects', this.sub(key), 'missingRequired');
      return undefined;
    }
    if (node.k === 'null') {
      if (spec?.nullable !== true) {
        if (spec?.required === 'phira') this.r.wrongType('phiraRejects', this.sub(key), kindName(spec), node);
        else this.r.wrongType(severityOf(spec), this.sub(key), kindName(spec), node);
      }
      return null;
    }
    return node;
  }

  /** An integer field, typed by the schema (i32 / u8 / u16 / u32). */
  int(key: string): number | undefined {
    const kind = intKindOf(this.kind, key);
    const node = this.get(key);
    if (node === undefined || node === null) return undefined;
    return readInt(this.r, node, this.sub(key), kind, severityOf(this.spec(key)));
  }

  num(key: string): number | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return undefined;
    return readNum(this.r, node, this.sub(key), severityOf(this.spec(key)));
  }

  str(key: string): string | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return undefined;
    if (node.k !== 'str') {
      this.r.wrongType(severityOf(this.spec(key)), this.sub(key), 'text', node);
      return undefined;
    }
    return readText(this.r, node, this.sub(key), severityOf(this.spec(key)));
  }

  bool(key: string): boolean | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return undefined;
    if (node.k !== 'bool') {
      this.r.wrongType(severityOf(this.spec(key)), this.sub(key), 'bool', node);
      return undefined;
    }
    return node.v;
  }

  triple(key: string): RpeTriple | undefined {
    const node = this.get(key);
    if (node === undefined) return undefined;
    if (node === null) return INVALID_TRIPLE;
    return readTriple(this.r, node, this.sub(key), severityOf(this.spec(key)));
  }

  rgb(key: string): readonly [number, number, number] | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return undefined;
    return readRgb(this.r, node, this.sub(key), severityOf(this.spec(key)));
  }

  /** Array node of `key` (undefined when absent, null when JSON null, wrong types reported and dropped). */
  arr(key: string): ArrNode | null | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return node;
    if (node.k !== 'arr') {
      this.r.wrongType(severityOf(this.spec(key)), this.sub(key), 'list', node);
      return undefined;
    }
    return node;
  }

  obj(key: string): ObjNode | null | undefined {
    const node = this.get(key);
    if (node === undefined || node === null) return node;
    if (node.k !== 'obj') {
      this.r.wrongType(severityOf(this.spec(key)), this.sub(key), 'object', node);
      return undefined;
    }
    return node;
  }
}

function kindName(spec: RpeFieldSpecEx | undefined): ExpectedKind {
  switch (spec?.kind) {
    case 'i32':
    case 'u8':
    case 'u16':
    case 'u32':
      return 'wholeNumber';
    case 'f32':
    case 'f64':
      return 'number';
    case 'bool':
      return 'bool';
    case 'string':
      return 'text';
    case 'triple':
      return 'beat';
    case 'rgb':
      return 'colour';
    case 'bezier4':
      return 'bezier4';
    case 'json':
    case undefined:
      return 'value';
  }
}

/**
 * A float field. A number beyond the f64 range (`1e400`) is reported ('numberOverflow': serde_json refuses it) and
 * returned as the infinity JSON.parse gives, which normalisation replaces.
 */
export function readNum(r: Reader, node: JsonNode, path: LazyPath, severity: DecodeSeverity): number | undefined {
  if (node.k !== 'num') {
    r.wrongType(severity, path, 'number', node);
    return undefined;
  }
  checkFinite(r, node, path, severity);
  return node.v;
}

/** Reports a number beyond the f64 range ('wrongType' / 'numberOverflow'); true when the number is finite. */
function checkFinite(r: Reader, node: NumNode, path: LazyPath, severity: DecodeSeverity): boolean {
  if (Number.isFinite(node.v)) return true;
  r.diag(severity, path, 'wrongType', node.lex, { reason: 'numberOverflow', expected: 'number', found: 'num' });
  return false;
}

/** An integer field: the raw value (possibly fractional or out of range, both reported). */
export function readInt(
  r: Reader,
  node: JsonNode,
  path: LazyPath,
  kind: IntKind,
  severity: DecodeSeverity,
): number | undefined {
  if (node.k !== 'num') {
    r.wrongType(severity, path, 'wholeNumber', node);
    return undefined;
  }
  checkIntLexeme(r, node, path, kind, severity);
  return node.v;
}

function checkIntLexeme(r: Reader, node: NumNode, path: LazyPath, kind: IntKind, severity: DecodeSeverity): void {
  if (!isSerdeIntegerLexeme(node)) r.diag(severity, path, 'floatInIntField', node.lex);
  const [lo, hi] = INT_RANGES[kind];
  if (node.v < lo) {
    if (lo === 0) r.diag(severity, path, 'negativeUnsigned', node.lex);
    else r.diag(severity, path, 'wrongType', node.lex, { reason: 'outOfRange' });
  } else if (node.v > hi) {
    r.diag(severity, path, 'wrongType', node.lex, { reason: 'outOfRange' });
  }
}

/** `[a, b, c]`: i32, u32, u32 (c = 0 is a 'note': Phira loads it but computes inf/NaN). */
export function readTriple(r: Reader, node: JsonNode, path: LazyPath, severity: DecodeSeverity): RpeTriple {
  const items = node.k === 'arr' ? node.items : null;
  const a = items?.[0];
  const b = items?.[1];
  const c = items?.[2];
  if (items?.length !== 3 || a?.k !== 'num' || b?.k !== 'num' || c?.k !== 'num') {
    r.wrongType(severity, path, 'beat', node);
    return INVALID_TRIPLE;
  }
  if (
    !isSerdeIntegerLexeme(a) ||
    !isSerdeIntegerLexeme(b) ||
    !isSerdeIntegerLexeme(c) ||
    a.v < -2147483648 ||
    a.v > 2147483647 ||
    b.v < 0 ||
    b.v > 4294967295 ||
    c.v < 0 ||
    c.v > 4294967295
  ) {
    checkIntLexeme(r, a, sub(path, 0), 'i32', severity);
    checkIntLexeme(r, b, sub(path, 1), 'u32', severity);
    checkIntLexeme(r, c, sub(path, 2), 'u32', severity);
  }
  if (c.v === 0) r.diag('note', path, 'tripleZeroDen', `[${a.lex}, ${b.lex}, ${c.lex}]`);
  return [a.v, b.v, c.v];
}

export function readRgb(
  r: Reader,
  node: JsonNode,
  path: LazyPath,
  severity: DecodeSeverity,
): readonly [number, number, number] | undefined {
  if (node.k !== 'arr' || node.items.length !== 3 || node.items.some((n) => n.k !== 'num')) {
    r.wrongType(severity, path, 'colour', node);
    return undefined;
  }
  const items = node.items as readonly [NumNode, NumNode, NumNode];
  items.forEach((n, i) => {
    checkIntLexeme(r, n, sub(path, i), 'u8', severity);
  });
  return [items[0].v, items[1].v, items[2].v];
}

function readBezier4(
  r: Reader,
  node: JsonNode,
  path: LazyPath,
  severity: DecodeSeverity,
): readonly [number, number, number, number] | undefined {
  if (node.k !== 'arr' || node.items.length !== 4 || node.items.some((n) => n.k !== 'num')) {
    r.wrongType(severity, path, 'bezier4', node);
    return undefined;
  }
  const items = node.items as readonly NumNode[];
  items.forEach((n, i) => {
    checkFinite(r, n, sub(path, i), severity);
  });
  const v = items.map((n) => n.v);
  return [v[0] ?? 0, v[1] ?? 0, v[2] ?? 0, v[3] ?? 0];
}

// ---------------------------------------------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------------------------------------------

export type EventValueKind = 'f32' | 'rgb' | 'string';
type EventValue = number | readonly [number, number, number] | string;

function defaultEventValue(kind: EventValueKind, list: EventListKey | null): EventValue {
  if (kind === 'rgb') return [255, 255, 255];
  if (kind === 'string') return '';
  return list === 'scaleXEvents' || list === 'scaleYEvents' ? 1 : 0;
}

function readEventValue(
  r: Reader,
  view: ObjView,
  key: 'start' | 'end',
  kind: EventValueKind,
  list: EventListKey | null,
): EventValue {
  const node = view.get(key);
  const def = defaultEventValue(kind, list);
  // Absent: reported as missing by view.get; null: reported as a wrong type by view.get.
  if (node === undefined || node === null) return def;
  const path = view.sub(key);
  if (kind === 'rgb') return readRgb(r, node, path, 'phiraRejects') ?? def;
  if (kind === 'string') {
    if (node.k === 'str') return readText(r, node, path, 'phiraRejects');
    r.wrongType('phiraRejects', path, 'text', node);
    return def;
  }
  return readNum(r, node, path, 'phiraRejects') ?? def;
}

/** One event object; null when the entry is not an object (Phira rejects the file; the entry is dropped). */
export function readEvent(
  r: Reader,
  node: JsonNode,
  path: LazyPath,
  kind: EventValueKind,
  list: EventListKey | null,
): RpeEvent<EventValue> | null {
  if (node.k !== 'obj') {
    r.wrongType('phiraRejects', path, 'event', node);
    return null;
  }
  const v = new ObjView(r, node, path, 'event');
  const startTime = v.triple('startTime') ?? [0, 0, 1];
  const endTime = v.triple('endTime') ?? [0, 0, 1];
  const ev: Mutable<RpeEvent<EventValue>> = {
    startTime,
    endTime,
    start: readEventValue(r, v, 'start', kind, list),
    end: readEventValue(r, v, 'end', kind, list),
    extra: v.extra,
  };
  const easingType = v.int('easingType');
  if (easingType !== undefined) ev.easingType = easingType;
  const easingLeft = v.num('easingLeft');
  if (easingLeft !== undefined) ev.easingLeft = easingLeft;
  const easingRight = v.num('easingRight');
  if (easingRight !== undefined) ev.easingRight = easingRight;
  const bezier = v.int('bezier');
  if (bezier !== undefined) ev.bezier = bezier;
  const bpNode = v.get('bezierPoints');
  if (bpNode !== undefined && bpNode !== null) {
    const bp = readBezier4(r, bpNode, v.sub('bezierPoints'), 'phiraRejects');
    if (bp !== undefined) ev.bezierPoints = bp;
  }
  const linkgroup = v.int('linkgroup');
  if (linkgroup !== undefined) ev.linkgroup = linkgroup;
  return ev;
}

/** An event list: entries that are not objects are dropped (reported). */
export function readEventList(
  r: Reader,
  node: ArrNode,
  path: LazyPath,
  list: EventListKey | null,
  kind: EventValueKind = list === null ? 'f32' : eventValueKind(list),
): RpeEvent<EventValue>[] {
  const out: RpeEvent<EventValue>[] = [];
  node.items.forEach((item, i) => {
    const ev = readEvent(r, item, sub(path, i), kind, list);
    if (ev !== null) out.push(ev);
  });
  return out;
}

function readListMember(r: Reader, view: ObjView, key: EventListKey): RpeEvent<EventValue>[] | null | undefined {
  const node = view.arr(key);
  if (node === undefined || node === null) return node;
  return readEventList(r, node, view.sub(key), key);
}

export function readLayer(r: Reader, node: ObjNode, path: LazyPath): RpeEventLayer {
  const v = new ObjView(r, node, path, 'layer');
  const layer: Mutable<RpeEventLayer> = { extra: v.extra };
  for (const key of LAYER_LIST_KEYS) {
    const list = readListMember(r, v, key);
    if (list !== undefined) (layer as Record<string, unknown>)[key] = list;
  }
  return layer;
}

export function readExtended(r: Reader, node: ObjNode, path: LazyPath): RpeExtended {
  const v = new ObjView(r, node, path, 'extended');
  const ext: Mutable<RpeExtended> = { extra: v.extra };
  for (const key of EXTENDED_LIST_KEYS) {
    const list = readListMember(r, v, key);
    if (list !== undefined) (ext as Record<string, unknown>)[key] = list;
  }
  return ext;
}

// ---------------------------------------------------------------------------------------------------------------
// Notes and controls
// ---------------------------------------------------------------------------------------------------------------

export function readNote(r: Reader, node: JsonNode, path: LazyPath): RpeNote | null {
  if (node.k !== 'obj') {
    r.wrongType('phiraRejects', path, 'note', node);
    return null;
  }
  const v = new ObjView(r, node, path, 'note');
  const startTime = v.triple('startTime') ?? [0, 0, 1];
  const note: Mutable<RpeNote> = {
    type: v.int('type') ?? 1,
    startTime,
    endTime: v.triple('endTime') ?? startTime,
    positionX: v.num('positionX') ?? 0,
    above: v.int('above') ?? 1,
    isFake: v.int('isFake') ?? 0,
    speed: v.num('speed') ?? 1,
    yOffset: v.num('yOffset') ?? 0,
    size: v.num('size') ?? 1,
    alpha: v.int('alpha') ?? 255,
    visibleTime: v.num('visibleTime') ?? 999999,
    extra: v.extra,
  };
  if (v.has('hitsound')) {
    const hs = v.get('hitsound');
    if (hs === null) note.hitsound = null;
    else {
      const s = v.str('hitsound');
      if (s !== undefined) note.hitsound = s;
    }
  }
  if (v.has('judgeArea')) {
    const ja = v.get('judgeArea');
    if (ja === null) note.judgeArea = null;
    else {
      const n = v.num('judgeArea');
      if (n !== undefined) note.judgeArea = n;
    }
  }
  for (const key of ['tint', 'tintHitEffects'] as const) {
    if (!v.has(key)) continue;
    const t = v.get(key);
    if (t === null) note[key] = null;
    else {
      const c = v.rgb(key);
      if (c !== undefined) note[key] = c;
    }
  }
  return note;
}

/** Value key of a control point: one of Phira's four curves, or 'skew' (RPE's skewControl, which Phira ignores). */
export type ControlPointValueKey = ControlValueKey | 'skew';

/**
 * One control point of a curve whose value key is `valueKey`. Phira deserialises `x` (f64) and `easing` (u8) and
 * flattens every other key into a HashMap<String, f32>: a missing value key panics, a non-numeric key fails, and
 * so does a number beyond the f64 range anywhere in the point (serde_json reads every key's number).
 *
 * `severity` 'note' reads a skewControl curve (Phira skips the whole list: every finding is a note, a missing
 * value key included); the default reads one of Phira's curves.
 */
export function readControlPoint(
  r: Reader,
  node: JsonNode,
  path: LazyPath,
  valueKey: ControlPointValueKey | null,
  severity: 'phiraRejects' | 'note' = 'phiraRejects',
): RpeControlPoint | null {
  if (node.k !== 'obj') {
    r.wrongType(severity, path, 'controlPoint', node);
    return null;
  }
  let x: number | undefined;
  let easing: number | undefined;
  let value: number | undefined;
  let seenX = false;
  let seenEasing = false;
  const extra = new Map<string, Json>();
  const nonNumeric = (p: LazyPath, item: JsonNode): void => {
    r.diag(severity, p, 'controlNonNumericExtra', scalarLexeme(item), { expected: 'number', found: item.k });
  };
  const duplicate = (p: LazyPath): void => {
    r.diag(severity, p, 'wrongType', undefined, { reason: 'duplicateKey' });
  };
  // skewControl is data Phira skips: a number beyond the f64 range holds the same stand-in as in a whole chart.
  const numberOf = (n: NumNode): number => (severity === 'note' && !Number.isFinite(n.v) ? overflowStandIn(n.v) : n.v);
  for (const [rawKey, item] of node.entries) {
    const key = readKey(r, rawKey, sub(path, toWellFormedString(rawKey)), severity);
    const p = sub(path, key);
    if (key === 'x') {
      // serde refuses a duplicate struct field even next to the flattened map.
      if (seenX) duplicate(p);
      seenX = true;
      const v = readNum(r, item, p, severity);
      x = v === undefined || item.k !== 'num' ? v : numberOf(item);
    } else if (key === 'easing') {
      if (seenEasing) duplicate(p);
      seenEasing = true;
      easing = readInt(r, item, p, intKindOf('control', 'easing'), severity);
    } else if (key === valueKey) {
      if (item.k === 'num') {
        checkFinite(r, item, p, severity);
        value = numberOf(item);
      } else nonNumeric(p, item);
    } else {
      if (item.k === 'num') checkFinite(r, item, p, severity);
      else nonNumeric(p, item);
      extra.delete(key);
      // A non-numeric extra was reported above and never reaches the IR: kept finite without a second finding.
      extra.set(key, item.k === 'num' ? numberOf(item) : passthroughJson(null, item, p, severity));
    }
  }
  if (!seenX) r.diag(severity, childPath(path, 'x'), 'missingRequired');
  if (!seenEasing) r.diag(severity, childPath(path, 'easing'), 'missingRequired');
  if (valueKey !== null && !node.entries.some(([k]) => k === valueKey)) {
    r.diag(severity === 'note' ? 'note' : 'phiraPanics', childPath(path, valueKey), 'controlMissingValueKey', valueKey);
  }
  return { x: x ?? 0, easing: easing ?? 1, value, extra: [...extra.entries()] };
}

export function readControls(
  r: Reader,
  node: ArrNode,
  path: LazyPath,
  valueKey: ControlPointValueKey | null,
  severity: 'phiraRejects' | 'note' = 'phiraRejects',
): RpeControlPoint[] {
  const out: RpeControlPoint[] = [];
  node.items.forEach((item, i) => {
    const p = readControlPoint(r, item, sub(path, i), valueKey, severity);
    if (p !== null) out.push(p);
  });
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Lines, META, BPM, chart
// ---------------------------------------------------------------------------------------------------------------

export function readAttachUi(r: Reader, node: JsonNode, path: LazyPath): UiElementName | null | undefined {
  if (node.k === 'null') return null;
  if (node.k === 'str') {
    if ((UI_ELEMENT_NAMES as readonly string[]).includes(node.v)) return node.v as UiElementName;
    const lower = node.v.toLowerCase();
    const match = UI_ELEMENT_NAMES.find((n) => n === lower);
    r.diag('phiraRejects', path, 'unknownAttachUI', scalarLexeme(node), { found: 'str' });
    return match ?? null;
  }
  if (node.k === 'num') {
    // RPE's UI numbers the elements 1-7 (F§10.5); Phira only accepts the names.
    const name = Number.isInteger(node.v) ? UI_ELEMENT_NAMES[node.v - 1] : undefined;
    r.diag('phiraRejects', path, 'unknownAttachUI', node.lex, { found: 'num' });
    return name ?? null;
  }
  r.diag('phiraRejects', path, 'unknownAttachUI', scalarLexeme(node), { found: node.k });
  return null;
}

/** The fields of a line other than eventLayers / extended / notes (shared with the 'line' fragment). */
export function readLineFields(
  r: Reader,
  v: ObjView,
): Omit<Mutable<RpeJudgeLine>, 'eventLayers' | 'extended' | 'notes'> {
  const line: Omit<Mutable<RpeJudgeLine>, 'eventLayers' | 'extended' | 'notes'> = {
    Name: v.str('Name') ?? 'Untitled',
    Texture: v.str('Texture') ?? 'line.png',
    isCover: v.int('isCover') ?? 1,
    extra: v.extra,
  };
  if (v.has('father')) {
    const n = v.get('father');
    if (n === null) line.father = null;
    else {
      const father = v.int('father');
      if (father !== undefined) line.father = father;
    }
  }
  if (v.has('rotateWithFather')) {
    const n = v.get('rotateWithFather');
    if (n === null) line.rotateWithFather = null;
    else {
      const b = v.bool('rotateWithFather');
      if (b !== undefined) line.rotateWithFather = b;
    }
  }
  const zOrder = v.int('zOrder');
  if (zOrder !== undefined) line.zOrder = zOrder;
  if (v.has('attachUI')) {
    const n = v.node('attachUI');
    if (n !== undefined) {
      const ui = readAttachUi(r, n, v.sub('attachUI'));
      if (ui !== undefined) line.attachUI = ui;
    }
  }
  for (const key of CONTROL_CURVE_KEYS) {
    const node = v.arr(key);
    if (node === undefined || node === null) continue;
    line[key] = readControls(r, node, v.sub(key), CONTROL_CURVES[key]);
  }
  // RPE-only members (Phira ignores them: wrong types are notes).
  const group = v.int('Group');
  if (group !== undefined) line.Group = group;
  const bpmfactor = v.num('bpmfactor');
  if (bpmfactor !== undefined) line.bpmfactor = bpmfactor;
  const anchorNode = v.get('anchor');
  if (anchorNode !== undefined && anchorNode !== null) {
    if (anchorNode.k === 'arr' && anchorNode.items.length === 2 && anchorNode.items.every((n) => n.k === 'num')) {
      const [ax, ay] = anchorNode.items as readonly [NumNode, NumNode];
      checkFinite(r, ax, sub(v.sub('anchor'), 0), 'note');
      checkFinite(r, ay, sub(v.sub('anchor'), 1), 'note');
      line.anchor = [ax.v, ay.v];
    } else {
      r.wrongType('note', v.sub('anchor'), 'xy', anchorNode);
    }
  }
  const isGif = v.bool('isGif');
  if (isGif !== undefined) line.isGif = isGif;
  const numOfNotes = v.int('numOfNotes');
  if (numOfNotes !== undefined) line.numOfNotes = numOfNotes;
  // skewControl is kept as written (RPE-only: Phira skips it, so a number beyond the f64 range there is a note).
  const skew = v.node('skewControl');
  if (skew !== undefined) line.skewControl = passthroughJson(r, skew, v.sub('skewControl'), 'note');
  return line;
}

export function readLine(r: Reader, node: JsonNode, path: LazyPath): RpeJudgeLine | null {
  if (node.k !== 'obj') {
    r.wrongType('phiraRejects', path, 'line', node);
    return null;
  }
  const v = new ObjView(r, node, path, 'line');
  const line: Mutable<RpeJudgeLine> = { ...readLineFields(r, v), eventLayers: [] };
  const layers = v.arr('eventLayers');
  if (layers !== undefined && layers !== null) {
    line.eventLayers = layers.items.map((item, i) => {
      const p = sub(v.sub('eventLayers'), i);
      if (item.k === 'null') return null;
      if (item.k !== 'obj') {
        r.wrongType('phiraRejects', p, 'layer', item);
        return null;
      }
      return readLayer(r, item, p);
    });
  }
  if (v.has('extended')) {
    const ext = v.obj('extended');
    if (ext === null) line.extended = null;
    else if (ext !== undefined) line.extended = readExtended(r, ext, v.sub('extended'));
  }
  if (v.has('notes')) {
    const notes = v.arr('notes');
    if (notes === null) line.notes = null;
    else if (notes !== undefined) {
      const list: RpeNote[] = [];
      notes.items.forEach((item, i) => {
        const n = readNote(r, item, sub(v.sub('notes'), i));
        if (n !== null) list.push(n);
      });
      line.notes = list;
    }
  }
  return line;
}

export function readBpm(r: Reader, node: JsonNode, path: LazyPath): RpeBpmItem | null {
  if (node.k !== 'obj') {
    r.wrongType('phiraRejects', path, 'bpm', node);
    return null;
  }
  const v = new ObjView(r, node, path, 'bpm');
  return { bpm: v.num('bpm') ?? 120, startTime: v.triple('startTime') ?? [0, 0, 1], extra: v.extra };
}

export function readMeta(r: Reader, node: ObjNode, path: LazyPath): RpeMeta {
  const v = new ObjView(r, node, path, 'meta');
  const meta: Mutable<RpeMeta> = { offset: v.int('offset') ?? 0, extra: v.extra };
  const ver = v.node('RPEVersion');
  // Phira reads RPEVersion as a JSON value first (F§2.2): a number beyond the f64 range there refuses the chart.
  if (ver !== undefined) meta.RPEVersion = passthroughJson(r, ver, v.sub('RPEVersion'), 'phiraRejects');
  for (const key of ['name', 'level', 'charter', 'composer', 'illustration', 'background', 'song', 'id'] as const) {
    const s = v.str(key);
    if (s !== undefined) meta[key] = s;
  }
  return meta;
}

export const DEFAULT_META: RpeMeta = Object.freeze({ offset: 0, extra: [] });

/**
 * The line that stands in for a judgeLineList entry that is not an object (Phira refuses such a chart): no events,
 * no notes, so it shows nothing, and every later line keeps its index.
 */
export function placeholderLine(): RpeJudgeLine {
  return { Name: 'Untitled', Texture: 'line.png', isCover: 1, eventLayers: [], extra: [] };
}

/** The whole chart object. */
export function readChart(r: Reader, root: JsonNode): RpeChart {
  if (root.k !== 'obj') {
    r.wrongType('phiraRejects', '', 'chart', root);
    return { META: DEFAULT_META, BPMList: [], judgeLineList: [], extra: [] };
  }
  const v = new ObjView(r, root, '', 'chart');
  const metaNode = v.obj('META');
  const chart: Mutable<RpeChart> = {
    META: metaNode === undefined || metaNode === null ? DEFAULT_META : readMeta(r, metaNode, 'META'),
    BPMList: [],
    judgeLineList: [],
    extra: v.extra,
  };
  const bpm = v.arr('BPMList');
  if (bpm !== undefined && bpm !== null) {
    const list: RpeBpmItem[] = [];
    bpm.items.forEach((item, i) => {
      const b = readBpm(r, item, sub('BPMList', i));
      if (b !== null) list.push(b);
    });
    chart.BPMList = list;
  }
  const lines = v.arr('judgeLineList');
  if (lines !== undefined && lines !== null) {
    const list: RpeJudgeLine[] = [];
    lines.items.forEach((item, i) => {
      // Kept as a placeholder: dropping it would move every later line (and its father references) by one.
      list.push(readLine(r, item, sub('judgeLineList', i)) ?? placeholderLine());
    });
    chart.judgeLineList = list;
  }
  const groups = v.arr('judgeLineGroup');
  if (groups !== undefined && groups !== null) {
    const names: string[] = [];
    groups.items.forEach((item, i) => {
      if (item.k === 'str') names.push(readText(r, item, childPath('judgeLineGroup', i), 'note'));
      else r.wrongType('note', childPath('judgeLineGroup', i), 'text', item);
    });
    chart.judgeLineGroup = names;
  }
  const tags = v.arr('timeTags');
  if (tags !== undefined && tags !== null) {
    const list: Array<{ readonly name: string; readonly time: RpeTriple }> = [];
    tags.items.forEach((item, i) => {
      const p = childPath('timeTags', i);
      if (item.k !== 'obj') {
        r.wrongType('note', p, 'timeTag', item);
        return;
      }
      let name: JsonNode | undefined;
      let time: JsonNode | undefined;
      const dropped = new Set<string>();
      for (const [k, value] of item.entries) {
        if (k === 'name') name = value;
        else if (k === 'time') time = value;
        else dropped.add(toWellFormedString(k));
      }
      // The decoded chart (and the IR) hold only a tag's name and time: other members are dropped, reported.
      for (const k of dropped) r.diag('note', childPath(p, k), 'wrongType', undefined, { reason: 'notKept' });
      if (name === undefined) r.diag('note', childPath(p, 'name'), 'missingRequired');
      else if (name.k !== 'str') r.wrongType('note', childPath(p, 'name'), 'text', name);
      if (time === undefined) r.diag('note', childPath(p, 'time'), 'missingRequired');
      if (name?.k !== 'str' || time === undefined) return;
      list.push({
        name: readText(r, name, childPath(p, 'name'), 'note'),
        time: readTriple(r, time, childPath(p, 'time'), 'note'),
      });
    });
    chart.timeTags = list;
  }
  const mls = v.str('multiLineString');
  if (mls !== undefined) chart.multiLineString = mls;
  const ms = v.num('multiScale');
  if (ms !== undefined) chart.multiScale = ms;
  const ct = v.num('chartTime');
  if (ct !== undefined) chart.chartTime = ct;
  const xy = v.bool('xybind');
  if (xy !== undefined) chart.xybind = xy;
  return chart;
}
